import Phaser from 'phaser';
import { FACTIONS, PLAYER_CLASSES, TRUTH_FRAGMENTS, THE_13_FALLEN } from '../data/GameConstants';
import Player from '../entities/Player';
import Enemy from '../entities/Enemy';

export default class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameScene' });
  }

  init() {
    this.playerFaction = this.registry.get('playerFaction') || 'human';
    this.playerClass = this.registry.get('playerClass') || 'warrior';
    this.currentLevel = 1;
    this.currentEpoch = 0;
    this.truthFragments = [];
    this.gold = 0;
    this.experience = 0;
  }

  create() {
    const { width, height } = this.cameras.main;

    // Create tilemap (placeholder grid)
    this.createMap();

    // Create player
    this.createPlayer();

    // Create enemies
    this.createEnemies();

    // Create UI
    this.createUI();

    // Input handling
    this.cursors = this.input.keyboard.createCursorKeys();
    this.wasd = this.input.keyboard.addKeys({
      up: Phaser.Input.Keyboard.KeyCodes.W,
      down: Phaser.Input.Keyboard.KeyCodes.S,
      left: Phaser.Input.Keyboard.KeyCodes.A,
      right: Phaser.Input.Keyboard.KeyCodes.D,
      action: Phaser.Input.Keyboard.KeyCodes.SPACE,
      menu: Phaser.Input.Keyboard.KeyCodes.ESC
    });

    // Camera
    this.cameras.main.startFollow(this.player, true, 0.08, 0.08);
    this.cameras.main.setZoom(1.5);

    // Collision detection
    this.physics.add.collider(this.player, this.walls);
    this.physics.add.overlap(this.player, this.enemies, this.handleCombat, null, this);
    this.physics.add.overlap(this.player, this.items, this.collectItem, null, this);

    // Pause menu
    this.input.keyboard.on('keydown-ESC', () => {
      this.scene.pause();
      this.scene.launch('PauseScene');
    });

    this.cameras.main.fadeIn(500, 0, 0, 0);
  }

  createMap() {
    const mapWidth = 50;
    const mapHeight = 50;
    const tileSize = 32;

    // Create ground layer
    this.ground = this.add.group();
    this.walls = this.physics.add.staticGroup();
    this.items = this.physics.add.group();

    for (let y = 0; y < mapHeight; y++) {
      for (let x = 0; x < mapWidth; x++) {
        const worldX = x * tileSize;
        const worldY = y * tileSize;

        // Border walls
        if (x === 0 || x === mapWidth - 1 || y === 0 || y === mapHeight - 1) {
          const wall = this.walls.create(worldX, worldY, 'wall_tile');
          wall.setOrigin(0);
        } else {
          // Ground
          const ground = this.add.image(worldX, worldY, 'ground_tile');
          ground.setOrigin(0);
          this.ground.add(ground);

          // Random walls (simple dungeon generation)
          if (Math.random() < 0.1 && x > 5 && y > 5) {
            const wall = this.walls.create(worldX, worldY, 'wall_tile');
            wall.setOrigin(0);
          }
        }
      }
    }

    // Add some truth fragments to collect
    for (let i = 0; i < 3; i++) {
      const x = Phaser.Math.Between(200, 1400);
      const y = Phaser.Math.Between(200, 1400);
      const fragment = this.items.create(x, y, 'truth_fragment');
      fragment.itemType = 'truth_fragment';
      fragment.fragmentIndex = i;
    }

    // Add health potions
    for (let i = 0; i < 5; i++) {
      const x = Phaser.Math.Between(200, 1400);
      const y = Phaser.Math.Between(200, 1400);
      const potion = this.items.create(x, y, 'health_potion');
      potion.itemType = 'health_potion';
    }
  }

  createPlayer() {
    const faction = FACTIONS[this.playerFaction.toUpperCase()];
    const playerClass = PLAYER_CLASSES[this.playerClass.toUpperCase()];

    this.player = new Player(this, 400, 400, 'player_placeholder', {
      faction: this.playerFaction,
      class: this.playerClass,
      stats: { ...playerClass.baseStats },
      level: 1
    });

    // Tint based on faction
    const factionColor = Phaser.Display.Color.HexStringToColor(faction.color).color;
    this.player.setTint(factionColor);
  }

  createEnemies() {
    this.enemies = this.physics.add.group();

    // Spawn some basic enemies
    for (let i = 0; i < 5; i++) {
      const x = Phaser.Math.Between(300, 1300);
      const y = Phaser.Math.Between(300, 1300);
      
      const enemy = new Enemy(this, x, y, 'enemy_placeholder', {
        type: 'anunnaki_soldier',
        level: this.currentLevel,
        stats: { hp: 50, attack: 10, defense: 5, speed: 60 }
      });
      
      this.enemies.add(enemy);
    }

    // Spawn a mini-boss
    const boss = new Enemy(this, 800, 800, 'boss_placeholder', {
      type: 'mini_boss',
      level: this.currentLevel + 5,
      stats: { hp: 200, attack: 25, defense: 15, speed: 40 },
      isBoss: true
    });
    boss.setScale(1.5);
    boss.setTint(0x9C27B0);
    this.enemies.add(boss);
  }

  createUI() {
    // Create UI container (fixed to camera)
    this.uiContainer = this.add.container(0, 0);
    this.uiContainer.setScrollFactor(0);
    this.uiContainer.setDepth(100);

    const { width, height } = this.cameras.main;

    // Health bar background
    const healthBg = this.add.rectangle(20, 20, 204, 24, 0x333333);
    healthBg.setOrigin(0);
    healthBg.setStrokeStyle(2, 0xFFFFFF);

    // Health bar
    this.healthBar = this.add.rectangle(22, 22, 200, 20, 0x4CAF50);
    this.healthBar.setOrigin(0);

    // Health text
    this.healthText = this.add.text(122, 20, '100/100', {
      font: '14px Manrope',
      fill: '#FFFFFF'
    }).setOrigin(0.5, 0);

    // Mana bar
    const manaBg = this.add.rectangle(20, 50, 154, 18, 0x333333);
    manaBg.setOrigin(0);
    manaBg.setStrokeStyle(1, 0xFFFFFF);

    this.manaBar = this.add.rectangle(22, 52, 150, 14, 0x2196F3);
    this.manaBar.setOrigin(0);

    // Level display
    this.levelText = this.add.text(20, 75, `Level: ${this.currentLevel}`, {
      font: 'bold 16px Manrope',
      fill: '#FFD700'
    });

    // Experience bar
    const expBg = this.add.rectangle(20, 100, 154, 12, 0x333333);
    expBg.setOrigin(0);
    this.expBar = this.add.rectangle(22, 102, 0, 8, 0xFFD700);
    this.expBar.setOrigin(0);

    // Faction indicator
    const faction = FACTIONS[this.playerFaction.toUpperCase()];
    this.factionText = this.add.text(20, 120, `Faction: ${faction.name}`, {
      font: '12px Manrope',
      fill: faction.color
    });

    // Truth fragments collected
    this.fragmentText = this.add.text(width / this.cameras.main.zoom - 150, 20, `Truth Fragments: 0/10`, {
      font: '14px Manrope',
      fill: '#FFD700'
    });

    // Gold (for Anunnaki)
    this.goldText = this.add.text(width / this.cameras.main.zoom - 150, 45, `Gold: ${this.gold}`, {
      font: '14px Manrope',
      fill: '#FFD700'
    });

    // Mini-map placeholder
    const miniMapBg = this.add.rectangle(width / this.cameras.main.zoom - 110, height / this.cameras.main.zoom - 110, 100, 100, 0x1A1A1A, 0.8);
    miniMapBg.setStrokeStyle(1, 0xFFD700);

    // Controls hint
    this.add.text(20, height / this.cameras.main.zoom - 30, 'WASD: Move | SPACE: Action | ESC: Menu', {
      font: '12px Manrope',
      fill: '#666666'
    });

    this.uiContainer.add([
      healthBg, this.healthBar, this.healthText,
      manaBg, this.manaBar,
      this.levelText, expBg, this.expBar,
      this.factionText, this.fragmentText, this.goldText,
      miniMapBg
    ]);
  }

  handleCombat(player, enemy) {
    if (this.combatCooldown) return;
    
    this.combatCooldown = true;
    this.time.delayedCall(500, () => {
      this.combatCooldown = false;
    });

    // Simple combat - damage both
    const playerDamage = Math.max(1, player.stats.attack - enemy.stats.defense / 2);
    const enemyDamage = Math.max(1, enemy.stats.attack - player.stats.defense / 2);

    enemy.takeDamage(playerDamage);
    player.takeDamage(enemyDamage);

    // Update UI
    this.updateHealthBar();

    // Show damage numbers
    this.showDamageNumber(enemy.x, enemy.y - 20, playerDamage, '#FFD700');
    this.showDamageNumber(player.x, player.y - 20, enemyDamage, '#FF6B6B');

    // Check if enemy died
    if (enemy.stats.hp <= 0) {
      this.defeatEnemy(enemy);
    }

    // Check if player died
    if (player.stats.hp <= 0) {
      this.gameOver();
    }

    // Knockback
    const angle = Phaser.Math.Angle.Between(player.x, player.y, enemy.x, enemy.y);
    enemy.setVelocity(Math.cos(angle) * 200, Math.sin(angle) * 200);
    player.setVelocity(-Math.cos(angle) * 100, -Math.sin(angle) * 100);
  }

  showDamageNumber(x, y, damage, color) {
    const text = this.add.text(x, y, `-${damage}`, {
      font: 'bold 18px Manrope',
      fill: color
    }).setOrigin(0.5);

    this.tweens.add({
      targets: text,
      y: y - 30,
      alpha: 0,
      duration: 800,
      onComplete: () => text.destroy()
    });
  }

  defeatEnemy(enemy) {
    // Grant experience
    const expGain = enemy.config.isBoss ? 100 : 25;
    this.experience += expGain;

    // Show exp gain
    this.showDamageNumber(enemy.x, enemy.y, `+${expGain} XP`, '#4CAF50');

    // Check level up
    const expNeeded = this.currentLevel * 100;
    if (this.experience >= expNeeded) {
      this.levelUp();
    }

    // Update exp bar
    this.expBar.width = (this.experience / expNeeded) * 150;

    // Drop gold
    if (Math.random() < 0.5) {
      this.gold += Phaser.Math.Between(5, 20);
      this.goldText.setText(`Gold: ${this.gold}`);
    }

    // Destroy enemy with effect
    this.tweens.add({
      targets: enemy,
      alpha: 0,
      scale: 0,
      duration: 300,
      onComplete: () => enemy.destroy()
    });
  }

  levelUp() {
    this.currentLevel++;
    this.experience = 0;

    // Increase stats
    const playerClass = PLAYER_CLASSES[this.playerClass.toUpperCase()];
    this.player.stats.hp += playerClass.growthRates.hp;
    this.player.stats.maxHp = this.player.stats.hp;
    this.player.stats.attack += playerClass.growthRates.attack;
    this.player.stats.defense += playerClass.growthRates.defense;

    // Visual feedback
    this.levelText.setText(`Level: ${this.currentLevel}`);
    
    const levelUpText = this.add.text(this.player.x, this.player.y - 50, 'LEVEL UP!', {
      font: 'bold 24px Manrope',
      fill: '#FFD700'
    }).setOrigin(0.5);

    this.tweens.add({
      targets: levelUpText,
      y: this.player.y - 80,
      alpha: 0,
      duration: 1500,
      onComplete: () => levelUpText.destroy()
    });

    // Flash effect
    this.cameras.main.flash(300, 255, 215, 0);

    this.updateHealthBar();
  }

  collectItem(player, item) {
    if (item.itemType === 'truth_fragment') {
      this.truthFragments.push(item.fragmentIndex);
      this.fragmentText.setText(`Truth Fragments: ${this.truthFragments.length}/10`);
      
      // Show collection message
      const fragmentNames = Object.values(TRUTH_FRAGMENTS);
      const fragment = fragmentNames[item.fragmentIndex];
      
      const text = this.add.text(player.x, player.y - 40, 
        fragment ? `Found: ${fragment.name}!` : 'Truth Fragment Found!', {
        font: 'bold 16px Manrope',
        fill: '#FFD700'
      }).setOrigin(0.5);

      this.tweens.add({
        targets: text,
        y: player.y - 70,
        alpha: 0,
        duration: 2000,
        onComplete: () => text.destroy()
      });

    } else if (item.itemType === 'health_potion') {
      const healAmount = 30;
      this.player.stats.hp = Math.min(this.player.stats.hp + healAmount, this.player.stats.maxHp);
      this.updateHealthBar();
      
      this.showDamageNumber(player.x, player.y, `+${healAmount}`, '#4CAF50');
    }

    // Remove item
    item.destroy();
  }

  updateHealthBar() {
    const healthPercent = this.player.stats.hp / this.player.stats.maxHp;
    this.healthBar.width = 200 * healthPercent;
    this.healthText.setText(`${Math.ceil(this.player.stats.hp)}/${this.player.stats.maxHp}`);

    // Color based on health
    if (healthPercent > 0.5) {
      this.healthBar.setFillStyle(0x4CAF50);
    } else if (healthPercent > 0.25) {
      this.healthBar.setFillStyle(0xFFD700);
    } else {
      this.healthBar.setFillStyle(0xF44336);
    }
  }

  gameOver() {
    this.cameras.main.fadeOut(1000, 0, 0, 0);
    this.time.delayedCall(1000, () => {
      this.scene.start('GameOverScene');
    });
  }

  update() {
    if (!this.player) return;

    // Movement
    const speed = this.player.stats.speed * 2;
    let velocityX = 0;
    let velocityY = 0;

    if (this.cursors.left.isDown || this.wasd.left.isDown) {
      velocityX = -speed;
    } else if (this.cursors.right.isDown || this.wasd.right.isDown) {
      velocityX = speed;
    }

    if (this.cursors.up.isDown || this.wasd.up.isDown) {
      velocityY = -speed;
    } else if (this.cursors.down.isDown || this.wasd.down.isDown) {
      velocityY = speed;
    }

    this.player.setVelocity(velocityX, velocityY);

    // Enemy AI
    this.enemies.getChildren().forEach(enemy => {
      if (enemy.active) {
        enemy.update(this.player);
      }
    });
  }
}
